import {
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
} from 'react'
import type { RefObject } from 'react'
import clsx from 'clsx'
import { Card, HeightAnimation } from '../../../../../components'
import type { SectionContainerContextState } from './SectionContainerContext'
import SectionContainerContext from './SectionContainerContext'
import type { FlexContainerAllProps as FlexContainerProps } from '../../../../../components/flex/Container'
import FieldBoundaryContext from '../../../DataContext/FieldBoundary/FieldBoundaryContext'
import withComponentMarkers from '../../../../../shared/helpers/withComponentMarkers'

export type ContainerMode = 'view' | 'edit' | 'auto'
export type SectionContainerProps = {
  /**
   * Defines the variant of the ViewContainer or EditContainer. Can be `outline`.
   * Defaults to `outline`.
   */
  variant?: 'outline' | 'basic' | 'filled'
}

export type SectionContainerAllProps = {
  mode: ContainerMode
  open?: boolean | undefined
  ariaLabel?: string
  omitFocusManagementRef?: RefObject<boolean | undefined>
} & SectionContainerProps

function SectionContainer(
  props: SectionContainerAllProps & FlexContainerProps
) {
  const {
    mode,
    open,
    ariaLabel,
    onAnimationEnd,
    className,
    children,
    variant = 'outline',
    omitFocusManagementRef = { current: undefined },
    ...restProps
  } = props

  const [, forceUpdate] = useReducer(() => ({}), {})

  const containerRef = useRef<HTMLDivElement>(undefined)
  const contextRef = useRef<
    SectionContainerContextState & {
      hasError?: boolean
      hasSubmitError?: boolean
    }
  >(undefined)
  contextRef.current = useContext(SectionContainerContext) || {}

  const { hasError, hasSubmitError } =
    useContext(FieldBoundaryContext) || {}
  contextRef.current.hasError = hasError
  contextRef.current.hasSubmitError = hasSubmitError
  const { containerMode, switchContainerMode, disableEditing } =
    contextRef.current

  const openRef = useRef(open ?? containerMode === mode)
  const setOpenState = useCallback((open: boolean) => {
    openRef.current = open
    forceUpdate()
  }, [])

  useEffect(() => {
    // - Set the open state, if it's controlled
    if (typeof open !== 'undefined') {
      setOpenState(open)
    } else {
      // - Open the block with animation, if it's in the right mode
      if (openRef.current !== (containerMode === mode)) {
        setOpenState(containerMode === mode)
      }
    }
  }, [containerMode, mode, open, setOpenState])

  // - Set the container mode to "edit" if we have an error
  useEffect(() => {
    if (disableEditing) {
      return
    }
    if (hasSubmitError && containerMode !== 'edit') {
      switchContainerMode?.('edit')
    }
  }, [disableEditing, hasSubmitError, containerMode, switchContainerMode])

  const setFocus = useCallback(
    (state) => {
      if (state === 'opened') {
        if (
          !omitFocusManagementRef.current &&
          !contextRef.current.hasSubmitError
        ) {
          containerRef?.current?.focus?.()
        }
        omitFocusManagementRef.current = false
      }
    },
    [omitFocusManagementRef]
  )

  // - Remove the block with animation, if it's in the right mode
  const handleAnimationEnd = useCallback(
    (state) => {
      setFocus(state)
      onAnimationEnd?.(state)
    },
    [onAnimationEnd, setFocus]
  )

  return (
    <HeightAnimation
      className={clsx(
        'dnb-forms-section-block',
        variant && `dnb-forms-section-block--variant-${variant}`,
        omitFocusManagementRef.current &&
          'dnb-forms-section-block--no-animation',
        contextRef.current.hasSubmitError &&
          'dnb-forms-section-block--error',
        className
      )}
      open={openRef.current}
      onAnimationEnd={handleAnimationEnd}
      duration={450}
      keepInDOM // Ensure fields get mounted so they will sync with the data context
    >
      <Card
        stack
        innerSpace={variant === 'basic' ? false : 'small'}
        filled={variant === 'filled'}
        className="dnb-forms-section-block__inner"
        {...restProps}
        aria-label={ariaLabel}
        ref={containerRef}
        tabIndex={-1}
      >
        {children}
      </Card>
    </HeightAnimation>
  )
}

withComponentMarkers(SectionContainer, {
  _supportsSpacingProps: true,
})

export default SectionContainer
